import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiUpdateUser, apiAddSkill, apiAdminResetPassword, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, AlertCircle, Lock, Key, RefreshCw, Copy, Check } from "lucide-react";

function rolesFor(actorRole, targetIsPermanent) {
    if (targetIsPermanent) return ["Super Admin"];
    if (actorRole === "Super Admin")
        return ["Super Admin", "Admin", "Project Manager", "Team Lead", "Consultant", "Client"];
    if (actorRole === "Admin")
        return ["Admin", "Project Manager", "Team Lead", "Consultant", "Client"];
    return [];
}

export default function EditUserDialog({
    open,
    onOpenChange,
    user,
    actor,
    projects,
    allSkills,
    onSaved,
    onSkillsChanged,
}) {
    const roleOptions = rolesFor(actor.role, user.is_permanent);
    const [role, setRole] = useState(user.role);
    const [active, setActive] = useState(user.active);
    const [selectedSkills, setSelectedSkills] = useState(user.skills || []);
    const [selectedProjects, setSelectedProjects] = useState(
        user.authorized_projects || [],
    );
    const [skillsMaster, setSkillsMaster] = useState(allSkills || []);
    const [newSkill, setNewSkill] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const isSelf = actor.user_id === user.user_id;
    const activeLocked = user.is_permanent || isSelf;

    function toggle(list, setList, value) {
        setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    }

    async function addSkill() {
        const s = newSkill.trim();
        if (!s) return;
        try {
            const next = await apiAddSkill(s);
            setSkillsMaster(next);
            onSkillsChanged?.(next);
            if (!selectedSkills.includes(s)) setSelectedSkills([...selectedSkills, s]);
            setNewSkill("");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function save() {
        setError("");
        setSaving(true);
        try {
            const patch = {};
            if (role !== user.role) patch.role = role;
            if (active !== user.active) patch.active = active;
            const skillsChanged =
                selectedSkills.length !== user.skills?.length ||
                selectedSkills.some((s) => !user.skills?.includes(s));
            if (skillsChanged) patch.skills = selectedSkills;
            const projectsChanged =
                selectedProjects.length !== user.authorized_projects?.length ||
                selectedProjects.some((p) => !user.authorized_projects?.includes(p));
            if (projectsChanged) patch.authorized_projects = selectedProjects;
            if (Object.keys(patch).length === 0) {
                onOpenChange(false);
                return;
            }
            await apiUpdateUser(user.user_id, patch);
            toast.success("User updated");
            onSaved?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    const activeProjects = (projects || []).filter((p) => p.active);

    // ---- Reset-password sub-section ----
    const canResetPassword =
        !user.is_permanent && (
            actor.role === "Super Admin" ||
            (actor.role === "Admin" && user.role !== "Super Admin")
        );
    const [resetOpen, setResetOpen] = useState(false);
    const [newPw, setNewPw] = useState("");
    const [forceChange, setForceChange] = useState(true);
    const [resetting, setResetting] = useState(false);
    const [copied, setCopied] = useState(false);

    function generatePassword() {
        const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
        const lower = "abcdefghijkmnopqrstuvwxyz";
        const digits = "23456789";
        const symbols = "!@#$%^&*-_";
        const pool = upper + lower + digits + symbols;
        function pick(s) {
            const buf = new Uint32Array(1);
            crypto.getRandomValues(buf);
            return s[buf[0] % s.length];
        }
        const bag = [pick(upper), pick(upper), pick(lower), pick(lower), pick(digits), pick(digits), pick(symbols)];
        for (let i = 0; i < 9; i++) bag.push(pick(pool));
        // Fisher-Yates
        for (let i = bag.length - 1; i > 0; i--) {
            const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
            [bag[i], bag[j]] = [bag[j], bag[i]];
        }
        setNewPw(bag.join(""));
        setCopied(false);
    }

    async function doReset() {
        if (!newPw || newPw.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setResetting(true);
        try {
            await apiAdminResetPassword(user.user_id, newPw, forceChange);
            toast.success("Password reset. Share the new password securely.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setResetting(false);
        }
    }

    async function copyPw() {
        try {
            await navigator.clipboard.writeText(newPw);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed. Select the value manually.");
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto"
                data-testid="edit-user-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Manage {user.full_name}
                        {user.is_permanent && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ntt-muted">
                                <Lock className="h-3 w-3" /> permanent
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>{user.email}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Role
                            </Label>
                            <Select
                                value={role}
                                onValueChange={setRole}
                                disabled={user.is_permanent || roleOptions.length === 0}
                            >
                                <SelectTrigger
                                    className="h-10"
                                    data-testid="edit-role-trigger"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {roleOptions.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {user.is_permanent && (
                                <p className="text-[11px] text-ntt-muted mt-1">
                                    The permanent Super Admin cannot be demoted.
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Active
                            </Label>
                            <div className="h-10 flex items-center gap-3 px-3 border border-ntt-border rounded-md bg-white">
                                <Switch
                                    checked={active}
                                    onCheckedChange={setActive}
                                    disabled={activeLocked}
                                    data-testid="edit-active-switch"
                                />
                                <span className="text-[13px]">
                                    {active ? "User can sign in" : "Sign-in disabled"}
                                </span>
                            </div>
                            {activeLocked && (
                                <p className="text-[11px] text-ntt-muted mt-1">
                                    {isSelf
                                        ? "You cannot deactivate your own account."
                                        : "The permanent Super Admin cannot be deactivated."}
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Skills
                        </Label>
                        <div
                            className="flex flex-wrap gap-1.5 mb-2"
                            data-testid="edit-skills-chips"
                        >
                            {skillsMaster.length === 0 && (
                                <span className="text-[12px] text-ntt-muted">
                                    No skills yet. Add one below.
                                </span>
                            )}
                            {skillsMaster.map((s) => {
                                const on = selectedSkills.includes(s);
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        data-testid={`skill-chip-${s}`}
                                        onClick={() =>
                                            toggle(
                                                selectedSkills,
                                                setSelectedSkills,
                                                s,
                                            )
                                        }
                                        className={`h-7 px-2.5 rounded-full text-[12px] border transition-colors ${
                                            on
                                                ? "bg-[#0072BC] text-white border-[#0072BC]"
                                                : "bg-white text-[#2B2F3A] border-ntt-border hover:border-[#0072BC]"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={newSkill}
                                onChange={(e) => setNewSkill(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addSkill();
                                    }
                                }}
                                placeholder="Add a new skill and press Enter"
                                data-testid="new-skill-input"
                                className="h-9"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addSkill}
                                data-testid="add-skill-btn"
                                className="h-9"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add
                            </Button>
                        </div>
                    </div>

                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Authorized projects
                        </Label>
                        {activeProjects.length === 0 ? (
                            <div className="text-[12px] text-ntt-muted border border-dashed border-ntt-border rounded-md px-3 py-3">
                                No active projects. Add projects on the Projects
                                screen first.
                            </div>
                        ) : (
                            <div
                                className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-auto pr-1"
                                data-testid="edit-projects-list"
                            >
                                {activeProjects.map((p) => {
                                    const checked = selectedProjects.includes(
                                        p.project_id,
                                    );
                                    return (
                                        <label
                                            key={p.project_id}
                                            className="flex items-center gap-2 text-[13px] border border-ntt-border rounded-md px-3 py-2 cursor-pointer hover:border-[#0072BC]"
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={() =>
                                                    toggle(
                                                        selectedProjects,
                                                        setSelectedProjects,
                                                        p.project_id,
                                                    )
                                                }
                                                data-testid={`edit-project-${p.project_id}`}
                                            />
                                            <span className="truncate">
                                                <span className="font-medium">
                                                    {p.name}
                                                </span>{" "}
                                                <span className="text-ntt-muted">
                                                    · {p.code}
                                                </span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {canResetPassword && (
                        <div
                            className="rounded-md border border-ntt-border p-4"
                            data-testid="reset-password-section"
                        >
                            <button
                                type="button"
                                onClick={() => setResetOpen((v) => !v)}
                                className="w-full flex items-center justify-between text-left"
                                data-testid="reset-password-toggle"
                            >
                                <div className="flex items-center gap-2">
                                    <Key className="h-4 w-4 text-[#0072BC]" />
                                    <span className="text-[13px] font-medium text-[#070F26]">
                                        Reset password
                                    </span>
                                </div>
                                <span className="text-[11px] text-ntt-muted">
                                    {resetOpen ? "hide" : "show"}
                                </span>
                            </button>
                            {resetOpen && (
                                <div className="mt-3 space-y-3">
                                    <div className="text-[12px] text-ntt-muted">
                                        Set a fresh password for {user.full_name}. Share it over a
                                        secure channel. The user{" "}
                                        {forceChange ? "must" : "may"} change it on next sign-in.
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={newPw}
                                            onChange={(e) => setNewPw(e.target.value)}
                                            placeholder="New password (min 8 characters)"
                                            data-testid="reset-password-input"
                                            className="h-9 font-mono text-[13px]"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={generatePassword}
                                            data-testid="reset-password-generate"
                                            className="h-9 text-[12px] whitespace-nowrap"
                                        >
                                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                                            Generate
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={copyPw}
                                            disabled={!newPw}
                                            data-testid="reset-password-copy"
                                            className="h-9 text-[12px]"
                                        >
                                            {copied ? (
                                                <Check className="h-3.5 w-3.5" />
                                            ) : (
                                                <Copy className="h-3.5 w-3.5" />
                                            )}
                                        </Button>
                                    </div>
                                    <label className="flex items-center gap-2 text-[12px] text-[#2B2F3A]">
                                        <Checkbox
                                            checked={forceChange}
                                            onCheckedChange={(v) => setForceChange(!!v)}
                                            data-testid="reset-password-force"
                                        />
                                        Force user to change on next sign-in
                                    </label>
                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            onClick={doReset}
                                            disabled={resetting || !newPw}
                                            data-testid="reset-password-save"
                                            className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                                        >
                                            {resetting ? "Resetting…" : "Reset password"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div
                            data-testid="edit-user-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="edit-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="edit-save-btn"
                        className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
